import * as SelectPrimitive from '@rn-primitives/select'
import * as React from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import Animated, { FadeIn } from 'react-native-reanimated'
import Icon from '~/components/Icon'
import { cn } from '~/lib/utils'

type Option = SelectPrimitive.Option

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

function SelectTrigger({
  ref,
  className,
  children,
  iconClassName,
  ...props
}: SelectPrimitive.TriggerProps & {
  ref?: React.RefObject<SelectPrimitive.TriggerRef>
  children?: React.ReactNode
  iconClassName?: string
}) {
  const triggerRef = React.useRef<SelectPrimitive.TriggerRef>(null)
  const effectiveRef = ref ?? triggerRef

  return (
    <SelectPrimitive.Trigger
      ref={effectiveRef}
      onTouchStart={() => {
        effectiveRef.current?.open()
      }}
      className={cn(
        'native:h-12 web:ring-offset-background web:focus:outline-none flex h-10 flex-row items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground [&>span]:line-clamp-1',
        props.disabled && 'web:cursor-not-allowed opacity-50',
        className
      )}
      {...props}>
      {children}
      <Icon name="chevron-down" size={18} className={cn('text-foreground', iconClassName)} />
    </SelectPrimitive.Trigger>
  )
}

/**
 * Platform: WEB ONLY
 */
function SelectScrollUpButton({ className, ...props }: SelectPrimitive.ScrollUpButtonProps) {
  if (Platform.OS !== 'web') {
    return null
  }
  return (
    <SelectPrimitive.ScrollUpButton
      className={cn('web:cursor-default flex items-center justify-center py-1', className)}
      {...props}>
      <Icon name="chevron-up" size={14} className="text-foreground" />
    </SelectPrimitive.ScrollUpButton>
  )
}

/**
 * Platform: WEB ONLY
 */
function SelectScrollDownButton({ className, ...props }: SelectPrimitive.ScrollDownButtonProps) {
  if (Platform.OS !== 'web') {
    return null
  }
  return (
    <SelectPrimitive.ScrollDownButton
      className={cn('web:cursor-default flex items-center justify-center py-1', className)}
      {...props}>
      <Icon name="chevron-down" size={14} className="text-foreground" />
    </SelectPrimitive.ScrollDownButton>
  )
}

function SelectContent({
  className,
  children,
  position = 'popper',
  portalHost,
  listClassName,
  ...props
}: SelectPrimitive.ContentProps & {
  ref?: React.RefObject<SelectPrimitive.ContentRef>
  className?: string
  listClassName?: string
  portalHost?: string
}) {
  const { open } = SelectPrimitive.useRootContext()

  return (
    <SelectPrimitive.Portal hostName={portalHost}>
      <SelectPrimitive.Overlay style={Platform.OS !== 'web' ? StyleSheet.absoluteFill : undefined}>
        <Animated.View className="z-50" entering={FadeIn}>
          <SelectPrimitive.Content
            className={cn(
              'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-32 rounded-lg border border-border bg-popover px-1 py-1 shadow-md shadow-foreground/10',
              position === 'popper' &&
                'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
              open
                ? 'web:animate-in web:fade-in-0 web:zoom-in-95'
                : 'web:animate-out web:fade-out-0 web:zoom-out-95',
              className
            )}
            position={position}
            {...props}>
            <SelectScrollUpButton />
            <SelectPrimitive.Viewport
              className={cn(
                position === 'popper' &&
                  'h-(--radix-select-trigger-height) min-w-(--radix-select-trigger-width) w-full',
                listClassName
              )}>
              {children}
            </SelectPrimitive.Viewport>
            <SelectScrollDownButton />
          </SelectPrimitive.Content>
        </Animated.View>
      </SelectPrimitive.Overlay>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: SelectPrimitive.LabelProps & {
  ref?: React.RefObject<SelectPrimitive.LabelRef>
}) {
  return (
    <SelectPrimitive.Label
      className={cn(
        'native:pb-2 native:pl-10 native:text-base py-1.5 pl-8 pr-2 font-semibold text-sm text-popover-foreground',
        className
      )}
      {...props}
    />
  )
}

function SelectItem({
  className,
  textClassName,
  ...props
}: SelectPrimitive.ItemProps & {
  ref?: React.RefObject<SelectPrimitive.ItemRef>
  textClassName?: string
}) {
  return (
    <SelectPrimitive.Item
      className={cn(
        'native:py-2 native:pl-10 web:cursor-default web:select-none web:outline-none group relative mb-1 flex flex-row items-center rounded-md py-2 pl-10 pr-4 last:mb-0 hover:bg-accent/50 hover:text-accent-foreground focus:bg-accent active:bg-accent data-[state=checked]:bg-accent',
        props.disabled && 'web:pointer-events-none opacity-50',
        className
      )}
      {...props}>
      <View className="native:left-3.5 native:pt-px absolute left-3.5 flex h-4 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Icon
            name="checkmark"
            size={16}
            className="text-popover-foreground group-hover:text-accent-foreground group-data-[state=checked]:text-accent-foreground"
          />
        </SelectPrimitive.ItemIndicator>
      </View>
      <SelectPrimitive.ItemText
        className={cn(
          'native:text-base/relaxed text-sm/relaxed text-popover-foreground group-hover:text-white group-focus:text-accent-foreground group-data-[state=checked]:text-accent-foreground',
          textClassName
        )}
      />
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.SeparatorProps & {
  ref?: React.RefObject<SelectPrimitive.SeparatorRef>
}) {
  return (
    <SelectPrimitive.Separator className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} />
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  type Option,
}
